import { gql } from 'graphql-tag';
import { TypedDocumentNode } from '@apollo/client';

import {
  GetProfileByExternalId,
  GetProfileByExternalIdVariables,
  GetUserIsFollowed,
  GetUserIsFollowedVariables,
  GetUsersCatches,
  GetUsersCatchesVariables,
  GetUsersPosts,
  GetUsersPostsVariables,
} from '@generated/graphql';
import { CatchFieldsForPostsViewer } from '@graphql/fragments/rutilus/CatchFieldsForPostsViewer';
import { ImageFragments } from '@graphql/fragments/rutilus/ImageType';
import { UsersPostsEdgeFieldsFragment } from '@graphql/fragments/rutilus/PostType';
import { UserFragments } from '@graphql/fragments/rutilus/UserType';

export const getProfileMetadata = gql`
  query GetProfileMetadata($nickname: String!) {
    user(nickname: $nickname) {
      ...UserId
      avatar(croppingStrategy: CENTER, height: 280, width: 280) {
        height
        width
        ...ImageFields
      }
      firstName
      lastName
      catches {
        totalCount
      }
    }
  }
  ${UserFragments.id}
  ${ImageFragments.imageFields}
`;

export const getProfile = gql`
  query GetProfile($nickname: String!) {
    user(nickname: $nickname) {
      ...ProfileUserFields
    }
  }
  ${UserFragments.profileUserFields}
`;

export const getProfileByExternalId: TypedDocumentNode<
  GetProfileByExternalId,
  GetProfileByExternalIdVariables
> = gql`
  query GetProfileByExternalId($externalId: String!) {
    user(externalId: $externalId) {
      ...ProfileUserFields
    }
  }
  ${UserFragments.profileUserFields}
`;

export const getUserIsFollowed: TypedDocumentNode<GetUserIsFollowed, GetUserIsFollowedVariables> =
  gql`
    query GetUserIsFollowed($nickname: String!) {
      user(nickname: $nickname) {
        ...UserId
        followedByCurrentUser
      }
    }
    ${UserFragments.id}
  `;

export const getUsersCatches: TypedDocumentNode<GetUsersCatches, GetUsersCatchesVariables> = gql`
  query GetUsersCatches($nickname: String!, $after: String, $first: Int) {
    user(nickname: $nickname) {
      ...UserId
      catches(
        after: $after
        first: $first
        filters: { withPhotos: true, orderByRecent: true, pinnedCatchesFirst: true }
      ) {
        totalCount
        pageInfo {
          startCursor
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...CatchFieldsForPostsViewer
          }
        }
      }
    }
  }
  ${UserFragments.id}
  ${CatchFieldsForPostsViewer}
`;

export const getUsersPosts: TypedDocumentNode<GetUsersPosts, GetUsersPostsVariables> = gql`
  query GetUsersPosts($nickname: String!, $after: String) {
    user(nickname: $nickname) {
      ...UserId
      posts(
        first: 15
        filters: { published: true, orderByRecent: true, topLevel: true }
        after: $after
      ) {
        pageInfo {
          startCursor
          hasNextPage
          endCursor
        }
        edges {
          ...UsersPostsEdgeFields
        }
      }
    }
  }
  ${UserFragments.id}
  ${UsersPostsEdgeFieldsFragment}
`;

export const getNickname = gql`
  query GetNickname($externalId: String!) {
    user(externalId: $externalId) {
      ...UserId
      nickname
    }
  }
  ${UserFragments.id}
`;

export const getNicknameById = gql`
  query GetNicknameById($id: Int!) {
    user(id: $id) {
      ...UserId
      nickname
    }
  }
  ${UserFragments.id}
`;
